import InfoIcon from '@mui/icons-material/Info';
import Tooltip from '@mui/material/Tooltip';
import { ChangeEvent, FC } from 'react';
import useReminderSchedule from './ReminderScheduleGroup.hook';
import {
  tableStyles,
  headerCellStyles,
  reminderNameStyles,
  reminderDescriptionStyles,
  sendTimeTextStyles,
  marketDefaultBadgeStyles,
  headerContainerStyles,
  marketInfoStyles,
  allRemindersLabelStyles,
  buttonsContainerStyles,
  cardHeaderStyles,
  cardHeaderTitleStyles,
  cardContentStyles,
} from './ReminderScheduleGroup.styles';
import Box from 'yoda-ui/components/Box';
import Button from 'yoda-ui/components/Button';
import { ButtonType } from 'yoda-ui/components/Button/Button.types';
import CircularProgress from 'yoda-ui/components/CircularProgress';
import Paper from 'yoda-ui/components/Paper';
import Switch from 'yoda-ui/components/Switch';
import Table from 'yoda-ui/components/Table';
import TableBody from 'yoda-ui/components/Table/TableBody';
import TableCell from 'yoda-ui/components/Table/TableCell';
import TableContainer from 'yoda-ui/components/Table/TableContainer';
import TableHead from 'yoda-ui/components/Table/TableHead';
import TableRow from 'yoda-ui/components/Table/TableRow';
import { YodaBorderRadius, YodaColors } from 'yoda-ui/yodaTheme';

const ReminderScheduleGroup: FC = () => {
  const {
    t,
    marketLabel,
    state,
    loading,
    saveLoading,
    resetLoading,
    handleAllRemindersToggle,
    handleReminderToggle,
    handleResetToDefaults,
    handleSaveConfiguration,
    getMarketDefaultLabel,
  } = useReminderSchedule();

  const handleAllRemindersSwitch = (_event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    handleAllRemindersToggle(checked);
  };

  const renderHeader = () => (
    <Box sx={ cardHeaderStyles }>
      <Box sx={ cardHeaderTitleStyles }>
        { t('reminder_schedule_title') }
      </Box>
      <Box sx={ headerContainerStyles }>
        {
          marketLabel && (
            <Box sx={ marketInfoStyles }>
              { t('reminder_schedule_market_defaults_loaded', { market: marketLabel }) }
            </Box>
          )
        }
        <Box sx={ allRemindersLabelStyles }>
          { t('reminder_schedule_all_reminders') }
        </Box>
        <Switch
          checked={ state.allRemindersEnabled }
          onSwitch={ handleAllRemindersSwitch }
          disabled={ loading }
        />
      </Box>
    </Box>
  );

  if (loading) {
    return (
      <Paper margin={ 0 } borderRadius={ YodaBorderRadius.medium }>
        { renderHeader() }
        <Box sx={ cardContentStyles }>
          <Box display="flex" justifyContent="center" padding={ 4 }>
            <CircularProgress />
          </Box>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper margin={ 0 } borderRadius={ YodaBorderRadius.medium }>
      { renderHeader() }
      <Box sx={ cardContentStyles }>
        <TableContainer>
          <Table sx={ tableStyles }>
            <TableHead>
              <TableRow>
                <TableCell sx={ headerCellStyles }>
                  { t('reminder_schedule_column_reminder') }
                </TableCell>
                <TableCell sx={ headerCellStyles }>
                  { t('reminder_schedule_column_send_time') }
                </TableCell>
                <TableCell sx={ headerCellStyles }>
                  { t('reminder_schedule_column_market_default') }
                </TableCell>
                <TableCell sx={ headerCellStyles } align="center">
                  { t('reminder_schedule_column_enabled') }
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {
                state.reminders.map((reminder) => {
                  const handleReminderSwitch = (
                    _event: ChangeEvent<HTMLInputElement>,
                    checked: boolean,
                  ) => {
                    handleReminderToggle(reminder.journeyId, checked);
                  };

                  return (
                    <TableRow key={ reminder.journeyId }>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Box sx={ reminderNameStyles(reminder.enabled) }>{ reminder.name }</Box>
                          {
                            (reminder.isSms || reminder.isWhatsapp) && reminder.supportedMarkets && (
                              <Tooltip

                                title={
                                  t(
                                    reminder.isSms ? 'reminder_schedule_sms_supported_countries'
                                      : 'reminder_schedule_whatsapp_supported_countries', {
                                      countries: reminder.supportedMarkets.join(', '),
                                    },
                                  )
                                }
                                placement="top"
                              >
                                <InfoIcon
                                  sx={ { fontSize: 16, marginLeft: 0.5, color: YodaColors.gray3 } }
                                />
                              </Tooltip>
                            )
                          }
                        </Box>
                        <Box sx={ reminderDescriptionStyles(reminder.enabled) }>
                          { reminder.sendDateDescription }
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={ sendTimeTextStyles }>
                          { reminder.sendTimeText }
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={ marketDefaultBadgeStyles(reminder.marketDefault) }>
                          { getMarketDefaultLabel(reminder.marketDefault) }
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Switch
                          checked={ reminder.enabled }
                          disabled={ reminder.isRequired || reminder.isDisabledForMarket }
                          onSwitch={ handleReminderSwitch }
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              }
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={ buttonsContainerStyles }>
          <Button
            buttonType={ ButtonType.secondary }
            onClick={ handleResetToDefaults }
            disabled={ resetLoading || saveLoading }
          >
            { resetLoading ? <CircularProgress size={ 20 } /> : t('reminder_schedule_reset_to_defaults') }
          </Button>
          <Button
            buttonType={ ButtonType.primary }
            onClick={ handleSaveConfiguration }
            disabled={ resetLoading || saveLoading }
          >
            { saveLoading ? <CircularProgress size={ 20 } /> : t('reminder_schedule_save_configuration') }
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default ReminderScheduleGroup;
